import { UserRole } from 'dy-frontend-http-repository/lib/data/enums';
import { TimeTrackingEntriesFilterInput } from 'dy-frontend-http-repository/lib/modules/TimeTacking/inputs';
import { TimeTrackingEntryResource } from 'dy-frontend-http-repository/lib/modules/TimeTacking/resources';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import { TextFormatUtils } from 'dy-frontend-shared/lib/utils';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import Avatar from '@app/components/Avatar';
import ConfirmationPopover from '@app/components/ConfirmationPopover';
import RouterLink from '@app/components/RouterLink';
import DevText from '@app/components/Text';
import { openModal } from '@app/containers/modals/store/events';
import { $authorizedUser } from '@app/containers/store/states';
import { Endpoints, imageHashPreview } from '@app/data/consts';
import { usePageTitle } from '@app/hooks';
import { Button, Card, Classes, Colors, Divider, Icon, Intent, Spinner, Switch } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import NonIdealState from '@components/NonIdealState';

import { $task } from '../../store/states';
import { formatSubTicketTitle } from '../TaskSubTickets/utils';

import CreateWorkReportModal, { CreateWorkReportModalProps } from './modals/CreateWorkReportModal';
import UpdateWorkReportModal, { UpdateWorkReportModalProps } from './modals/UpdateWorkReportModal';
import { timeTrackingEntriesApi } from './store/apis';
import { fetchWorkReports, removeWorkReport } from './store/effects';
import { resetWorkReports } from './store/events';
import { $workReports } from './store/states';

import { workReportTypesInformation } from './consts';

const adminAccessRoles = [UserRole.CORE_ROOT, UserRole.CORE_ADMIN, UserRole.CORE_PLATFORM_DEV];

// This needed for showing create work report button for roles which are excluded from the task participation calculation
const ignoreTaskParticipationRoles = [
    UserRole.CORE_OPERATIONS_MANAGER,
    UserRole.CORE_ACCOUNT_DIRECTOR,
    UserRole.CORE_CREATIVE_DIRECTOR,
    UserRole.CORE_HR,
    UserRole.CORE_ASSISTANT_ART_DIRECTOR,
    UserRole.CORE_ART_DIRECTOR,
    UserRole.CORE_PROJECT_MANAGER,
    UserRole.CORE_ACCOUNT_MANAGER,
];

const allowWorkReportCreationRoles = [
    ...adminAccessRoles,
    UserRole.CORE_OPERATIONS_MANAGER,
    UserRole.CORE_ACCOUNT_DIRECTOR,
    UserRole.CORE_CREATIVE_DIRECTOR,
    UserRole.CORE_ACCOUNT_MANAGER,
    UserRole.CORE_HR,
    UserRole.CORE_ASSISTANT_ART_DIRECTOR,
    UserRole.CORE_ART_DIRECTOR,
    UserRole.CORE_PROJECT_MANAGER,
    UserRole.CORE_DESIGNER,
];

const allowWorkReportFullVisibilityRoles = [
    ...adminAccessRoles,
    UserRole.CORE_OPERATIONS_MANAGER,
    UserRole.CORE_ACCOUNT_DIRECTOR,
    UserRole.CORE_CREATIVE_DIRECTOR,
    UserRole.CORE_HR,
    UserRole.CORE_ASSISTANT_ART_DIRECTOR,
    UserRole.CORE_ART_DIRECTOR,
    UserRole.CORE_ACCOUNT_MANAGER,
];

const allowWorkReportRemovalRoles = [
    ...adminAccessRoles,
    UserRole.CORE_OPERATIONS_MANAGER,
    UserRole.CORE_ACCOUNT_DIRECTOR,
    UserRole.CORE_CREATIVE_DIRECTOR,
    UserRole.CORE_HR,
    UserRole.CORE_ASSISTANT_ART_DIRECTOR,
    UserRole.CORE_ART_DIRECTOR,
];

const allowWorkReportEditingRoles = [
    ...adminAccessRoles,
    UserRole.CORE_OPERATIONS_MANAGER,
    UserRole.CORE_ACCOUNT_DIRECTOR,
    UserRole.CORE_CREATIVE_DIRECTOR,
    UserRole.CORE_HR,
    UserRole.CORE_ASSISTANT_ART_DIRECTOR,
    UserRole.CORE_ART_DIRECTOR,
];

const TaskWorkReports: React.FC = () => {
    const { changeTitle } = usePageTitle('Work Reports');

    const me = useStore($authorizedUser);
    const task = useStore($task);
    const workReports = useStore($workReports);

    const [showMyReports, setShowMyReports] = useState(false);
    const [workReportRemovingId, setWorkReportRemovingId] = useState<ID | null>(null);

    const isFetchingAllEntriesAllowed =
        adminAccessRoles.includes(me?.user.role) || allowWorkReportFullVisibilityRoles.includes(me?.user.role);

    useEffect(() => {
        if (!task || !me) {
            return;
        }

        // Get actor user ID
        let actorUserId: ID | undefined = me.user.id;

        if (isFetchingAllEntriesAllowed) {
            if (showMyReports) {
                actorUserId = me.user.id;
            } else {
                actorUserId = undefined;
            }
        } else {
            actorUserId = me.user.id;
        }

        // Create filter
        const workReportsFilterInput: TimeTrackingEntriesFilterInput = { task_id: task.id, actor_user_id: actorUserId };

        // Fetch entries
        fetchWorkReports({ filter: workReportsFilterInput }).catch((e) => {
            // TODO: error
            console.error(e);
        });
    }, [task, me, showMyReports]);

    useEffect(() => {
        if (!task) {
            return;
        }

        changeTitle(`Work Reports - ${task.title}`);
    }, [task?.title]);

    useEffect(() => {
        return () => {
            resetWorkReports();
        };
    }, []);

    if (!me || !task) {
        return null;
    }

    const getDurationInHoursAndMinutesFormat = (duration: number) => {
        const hours = Math.trunc(duration / 60);
        const minutes = duration - 60 * hours;

        return { hours: `${hours}`, minutes: `${minutes}`.padStart(2, '0') };
    };

    const handleRemoveWorkReport = async (id: ID) => {
        setWorkReportRemovingId(id);

        try {
            // Remove work report
            await removeWorkReport(id);

            // Remove work report from local state
            timeTrackingEntriesApi.remove(id);
        } catch (e) {
            // TODO: handle error
            console.error(e);
        } finally {
            setWorkReportRemovingId(null);
        }
    };

    const renderGroupHeader = (isFirstGroup: boolean, date: string) => {
        const entryDateAt = moment(date);

        const renderDateSuffix = () => {
            const commonSuffixProps = { inline: true, muted: true, className: 'ml-2' };
            const isEntryDateAtToday = entryDateAt.isSame(moment(), 'day');

            if (isEntryDateAtToday) {
                return <DevText {...commonSuffixProps}>Today</DevText>;
            }

            return <DevText {...commonSuffixProps}>{TextFormatUtils.capitalize(entryDateAt.fromNow())}</DevText>;
        };

        return (
            <Heading type="h5" className={['mb-1', isFirstGroup ? '' : 'mt-3'].join(' ')}>
                {entryDateAt.format('D MMMM YYYY')}
                {renderDateSuffix()}
            </Heading>
        );
    };

    // Which phase the time was booked against, when it was booked against one at all.
    //
    // Time logged against the Request itself carries no phase, and a heading saying so would be
    // noise on the majority of rows — so those rows render exactly as they always have. The
    // presentation matches the sub-task card character for character, so a reader moving between
    // the two tabs is looking at the same title.
    const renderSubTaskHeading = (entry: TimeTrackingEntryResource) => {
        const scopeSummary = entry.task_sub_ticket_scope_summary;

        if (!scopeSummary || scopeSummary.trim().length === 0) {
            return null;
        }

        const subTaskTitle = formatSubTicketTitle({ scopeSummary, fallback: 'Sub-task' });

        return (
            <Heading className="mb-1" type="h6" muted title={subTaskTitle.full} aria-label={subTaskTitle.full}>
                {subTaskTitle.display}
            </Heading>
        );
    };

    const renderWorkReport = (
        isFirstEntry: boolean,
        shouldRenderGroupHeader: boolean,
        entry: TimeTrackingEntryResource
    ) => {
        // Get actor user
        const actor = entry.actor_user;

        // Get avatar src
        let avatarSrc: string | null = null;
        if (actor.image_hash) {
            avatarSrc = imageHashPreview.userImage(actor.image_hash, ImageHashPreviewSize.XS);
        }

        const { hours, minutes } = getDurationInHoursAndMinutesFormat(entry.duration_min);

        return (
            <>
                {shouldRenderGroupHeader && renderGroupHeader(isFirstEntry, entry.date_at)}

                <Card key={entry.id}>
                    {renderSubTaskHeading(entry)}

                    <Flex direction="row" justify="space-between" align="flex-end">
                        <Flex fullWidth direction="column" align="stretch">
                            <Flex direction="row" justify="space-between">
                                <Flex direction="row" align="center">
                                    <Avatar
                                        className="mr-1"
                                        width="35px"
                                        height="35px"
                                        alt={`${actor.first_name} ${actor.last_name}`}
                                        src={avatarSrc}
                                    />

                                    <DevText>
                                        <RouterLink
                                            color={Colors.WHITE}
                                            to={Endpoints.CORE_USER_TASKS.replace(':coreUserId', actor.id)}
                                        >
                                            {actor.first_name} {actor.last_name}
                                        </RouterLink>{' '}
                                        <DevText inline muted>
                                            worked on
                                        </DevText>{' '}
                                        {workReportTypesInformation[entry.type] ?? 'unknown'}{' '}
                                        <DevText inline muted>
                                            for
                                        </DevText>{' '}
                                        {hours}h {minutes}
                                        min
                                    </DevText>
                                </Flex>

                                <Flex direction="row">
                                    {renderEditWorkReportButton(entry)}
                                    {renderRemoveWorkReportButton(entry.id)}
                                </Flex>
                            </Flex>

                            {entry.comment.trim().length > 0 && <DevText className="mt-1">{entry.comment}</DevText>}
                        </Flex>
                    </Flex>
                </Card>
            </>
        );
    };

    const renderWorkReports = () => {
        // Work reports were NOT fetched yet
        if (!workReports) {
            return (
                <Flex justify="center">
                    <Spinner />
                </Flex>
            );
        }

        // Work reports were fetched, but where are no entries exist
        if (workReports.items.length === 0) {
            return (
                <NonIdealState
                    icon={<Icon className="mb-2" icon="search" size={70} />}
                    title={
                        <Heading type="h4" className="mb-1">
                            No work reports found
                        </Heading>
                    }
                />
            );
        }

        const shouldRenderGroupHeader = (currentEntry: TimeTrackingEntryResource, index: number) => {
            if (index === 0) {
                return true;
            }

            const previousEntry = workReports.items[index - 1];

            const previousEntryDateAt = moment(previousEntry.date_at);
            const currentEntryDateAt = moment(currentEntry.date_at);

            if (previousEntryDateAt.isSame(currentEntryDateAt, 'day')) {
                // Same day, no need to show new date header for entries group
                return false;
            }

            return true;
        };

        return workReports.items.map((entry, index) => (
            <div className="mb-2" key={entry.id}>
                {renderWorkReport(index === 0, shouldRenderGroupHeader(entry, index), entry)}
            </div>
        ));
    };

    const renderWorkReportsTotalDuration = () => {
        if (!workReports || workReports.items.length === 0) {
            return null;
        }

        // Get total duration for all entries
        let totalDuration = 0;
        workReports.items.forEach((entry) => (totalDuration += entry.duration_min));

        //  Convert duration to hours and minutes
        const { hours, minutes } = getDurationInHoursAndMinutesFormat(totalDuration);

        return (
            <div className="mb-2">
                <Flex direction="row" justify="space-between">
                    <Heading type="h4">Total time spent:</Heading>
                    <Heading type="h4">
                        {hours}h {minutes}min
                    </Heading>
                </Flex>

                <Divider className="mt-2" />
            </div>
        );
    };

    const renderCreateWorkReportButton = () => {
        const isMeParticipant = !!task.participants.find((p) => p.user.id === me.user.id);
        const isCreationAllowed =
            adminAccessRoles.includes(me.user.role) ||
            ((isMeParticipant || ignoreTaskParticipationRoles.includes(me.user.role)) &&
                allowWorkReportCreationRoles.includes(me.user.role));

        if (!isCreationAllowed) {
            return null;
        }

        return (
            <Button
                className="ml-auto"
                icon="add"
                intent={Intent.PRIMARY}
                onClick={() =>
                    openModal<CreateWorkReportModalProps>({
                        ModalComponent: CreateWorkReportModal,
                        data: { taskId: task.id, actorUserId: me.user.id },
                    })
                }
            >
                Create report
            </Button>
        );
    };

    const renderRemoveWorkReportButton = (id: ID) => {
        const isRemovalAllowed = allowWorkReportRemovalRoles.includes(me.user.role);
        const isButtonLoading = workReportRemovingId !== null && workReportRemovingId === id;
        const isButtonDisabled = workReportRemovingId !== null;

        if (!isRemovalAllowed) {
            return null;
        }

        return (
            <ConfirmationPopover
                title="Are you sure want to remove work report"
                actions={[
                    <Button
                        intent={Intent.DANGER}
                        className={Classes.POPOVER_DISMISS}
                        onClick={() => handleRemoveWorkReport(id)}
                    >
                        Yes, remove
                    </Button>,
                ]}
            >
                <Button
                    loading={isButtonLoading}
                    disabled={isButtonDisabled}
                    icon="trash"
                    className="ml-1"
                    intent={Intent.DANGER}
                >
                    Remove
                </Button>
            </ConfirmationPopover>
        );
    };

    const renderEditWorkReportButton = (workReport: TimeTrackingEntryResource) => {
        const isEditAllowed = allowWorkReportEditingRoles.includes(me.user.role);

        if (!isEditAllowed) {
            return null;
        }

        return (
            <Button
                icon="edit"
                onClick={() =>
                    openModal<UpdateWorkReportModalProps>({
                        ModalComponent: UpdateWorkReportModal,
                        data: {
                            taskId: task.id,
                            durationMin: workReport.duration_min,
                            comment: workReport.comment,
                            type: workReport.type,
                            workReportId: workReport.id,
                        },
                    })
                }
            >
                Update
            </Button>
        );
    };

    return (
        <div>
            {renderWorkReportsTotalDuration()}

            <Flex className="mb-2" justify="space-between" align="center">
                {isFetchingAllEntriesAllowed && (
                    <Switch
                        checked={showMyReports}
                        label="Show only my reports"
                        onChange={() => setShowMyReports((prevValue) => !prevValue)}
                    />
                )}

                {renderCreateWorkReportButton()}
            </Flex>

            {renderWorkReports()}
        </div>
    );
};

export default TaskWorkReports;
